import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { StoryPublishStatus } from "../services/dataStories/storiesApi.types";
import type { StorySqlChart, StorySqlTextItem } from "../components/organisms/StorySqlDialog";
import type { SaveStatusLabelStatus } from "../components/molecules/SaveStatusLabel";
import { useStoryPath } from "../contexts/StoryPathContext";

/**
 * Chrome the Story Detail page publishes into the global Header while mounted.
 * Mirrors the `KgHeaderContext` bridge pattern: the page (rendered in Shell's
 * `main`) writes the bridge; the Header (a sibling in the Shell) reads it.
 *
 * Scope: the dynamic left-side branding (workspace overline + story title +
 * initials badge). Right-side actions are static chrome rendered by the Header.
 */
export interface StoryHeaderBridge {
  /** Workspace name — rendered as the uppercase overline above the title. */
  workspaceName: string;
  /** Story title — "Untitled" upstream when the story is unsaved. */
  storyTitle: string;
  /** Explicit badge initials; falls back to workspace-derived initials. */
  badgeInitials?: string;
  /** v2 publish state — drives the status pill; absent while loading or after a failed load. */
  publishStatus?: StoryPublishStatus;
  /** Requests a Draft ↔ Published change; the page owns the optimistic mutation. */
  onPublishStatusSelect?: (publishStatus: StoryPublishStatus) => void;
  /** Backend message from a failed publish/unpublish; the Header shows it as a Toast. */
  publishStatusErrorMessage?: string | null;
  /** Clears `publishStatusErrorMessage` once its Toast closes. */
  onPublishStatusErrorDismiss?: () => void;
  /**
   * Gates the Share pill. False until the story is generated AND the canvas
   * has at least one fully-rendered widget (plan page / polling / empty
   * canvas all stay disabled).
   */
  isShareEnabled?: boolean;
  /** The story is still being generated: the mode switch locks Present and Explore. */
  isGenerating?: boolean;
  /**
   * Registered by StoryPresentCenter — triggers a fresh PDF export from the
   * canvas and returns the generated File. Header passes this to DeliverStoryFlow
   * so Slack delivery uses a live capture instead of the last stored artifact.
   */
  onExportRequest?: (format: string) => Promise<File | null>;
  /**
   * Opens the real export (format, theme, preview), published by StoryPresentCenter while it is mounted.
   * Absent on pages that cannot export, where the header's Export item waits as Coming soon.
   */
  onOpenExport?: () => void;
  /** Charts View SQL (⋯ menu, ⌘K) can show; the page publishes them. */
  sqlCharts?: StorySqlChart[];
  /** The story's text tiles, listed in View SQL as "No query" rows. */
  sqlTextItems?: StorySqlTextItem[];
  /** The chart open in the editor, if any: View SQL starts there. */
  openEditorItemId?: string | null;
  /** The page's story is on the new path. The header sits outside the page's StoryPathProvider, so it reads the path here. */
  sqlIsNewPath?: boolean;
  /** Canvas undo/redo, published by the page from inside its history provider. */
  history?: StoryHistorySlot;
  /** The story-level AI summary text, published by the canvas; seeds Deliver's default message. */
  storySummary?: string;
}

export interface StoryHistorySlot {
  undo: () => void | Promise<void>;
  redo: () => void | Promise<void>;
  canUndo: boolean;
  canRedo: boolean;
}

type StoryHeaderReadContext = { bridge: StoryHeaderBridge | null };
type BridgeUpdater = StoryHeaderBridge | null | ((prev: StoryHeaderBridge | null) => StoryHeaderBridge | null);
type StoryHeaderWriteContext = (updater: BridgeUpdater) => void;

const StoryHeaderContext = createContext<StoryHeaderReadContext | null>(null);
// Separate write context — the page only writes, so it does not subscribe to
// bridge changes and re-render on its own publishes.
const StoryHeaderSetContext = createContext<StoryHeaderWriteContext | null>(
  null,
);

// Separate context for onExportRequest so it survives bridge being null
// (child effects fire before parent effects, so StoryPresentCenter registers
// before StoryPresentPage sets the bridge — a plain bridge merge would be lost).
const ExportRequestContext = createContext<{
  onExportRequest: ((format: string) => Promise<File | null>) | null;
  setOnExportRequest: (fn: ((format: string) => Promise<File | null>) | null) => void;
} | null>(null);

// View SQL's charts come from the canvas, a child of the page that sets the bridge, so they live in their
// own slot (like onExportRequest): a whole-bridge write from the page never drops them, and a child
// effect that runs before the page's still lands.
type StorySqlSlot = Pick<StoryHeaderBridge, "sqlCharts" | "openEditorItemId" | "sqlTextItems" | "sqlIsNewPath">;
const StorySqlContext = createContext<((slot: StorySqlSlot | null) => void) | null>(null);

// The open-export handler comes from the present page's centre, so it too has its own slot.
const StoryOpenExportContext = createContext<((open: (() => void) | null) => void) | null>(null);

const StoryHistoryContext = createContext<((slot: StoryHistorySlot | null) => void) | null>(null);

// The story summary comes from the canvas too, so it gets its own slot for the same reason.
const StorySummaryContext = createContext<((summary: string | null) => void) | null>(null);

export const StoryHeaderProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [bridge, setBridgeState] = useState<StoryHeaderBridge | null>(null);
  const [onExportRequest, setOnExportRequestRaw] = useState<((format: string) => Promise<File | null>) | null>(null);
  // useState treats functions as lazy initialisers, so we wrap in an object to store function references.
  const setOnExportRequest = useCallback(
    (fn: ((format: string) => Promise<File | null>) | null) => {
      setOnExportRequestRaw(() => fn);
    },
    [],
  );
  const setBridge = useCallback((updater: BridgeUpdater) => {
    if (typeof updater === "function") {
      setBridgeState(updater);
    } else {
      setBridgeState(updater);
    }
  }, []);
  const [sqlSlot, setSqlSlot] = useState<StorySqlSlot | null>(null);
  const [historySlot, setHistorySlot] = useState<StoryHistorySlot | null>(null);
  const [summarySlot, setSummarySlot] = useState<string | null>(null);
  const [openExport, setOpenExportRaw] = useState<(() => void) | null>(null);
  const setOpenExport = useCallback((fn: (() => void) | null) => setOpenExportRaw(() => fn), []);
  const readValue = useMemo(
    () => ({
      bridge: bridge
        ? {
            ...bridge,
            ...(sqlSlot ?? {}),
            history: historySlot ?? undefined,
            storySummary: summarySlot ?? undefined,
            onOpenExport: openExport ?? undefined,
            onExportRequest: onExportRequest ?? undefined,
          }
        : null,
    }),
    [bridge, sqlSlot, historySlot, summarySlot, openExport, onExportRequest],
  );
  const exportCtxValue = useMemo(
    () => ({ onExportRequest, setOnExportRequest }),
    [onExportRequest, setOnExportRequest],
  );
  return (
    <StoryHeaderSetContext.Provider value={setBridge}>
      <ExportRequestContext.Provider value={exportCtxValue}>
        <StorySqlContext.Provider value={setSqlSlot}>
          <StoryHistoryContext.Provider value={setHistorySlot}>
            <StorySummaryContext.Provider value={setSummarySlot}>
              <StoryOpenExportContext.Provider value={setOpenExport}>
                <StoryHeaderContext.Provider value={readValue}>
                  <StoryHeaderSaveStateProvider>{children}</StoryHeaderSaveStateProvider>
                </StoryHeaderContext.Provider>
              </StoryOpenExportContext.Provider>
            </StorySummaryContext.Provider>
          </StoryHistoryContext.Provider>
        </StorySqlContext.Provider>
      </ExportRequestContext.Provider>
    </StoryHeaderSetContext.Provider>
  );
};

export function useStoryHeaderBridge(): StoryHeaderBridge | null {
  const ctx = useContext(StoryHeaderContext);
  return ctx?.bridge ?? null;
}

/**
 * Registers a PDF export callback from StoryPresentCenter into the header bridge.
 * Uses a separate context so registration succeeds even when the bridge hasn't
 * been initialized yet (React child effects run before parent effects, so the
 * bridge may still be null when this runs).
 */
export function useRegisterExportCallback(
  onExportRequest: ((format: string) => Promise<File | null>) | null,
): void {
  const ctx = useContext(ExportRequestContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setOnExportRequest(onExportRequest);
    return () => {
      ctx.setOnExportRequest(null);
    };
  }, [onExportRequest, ctx]);
}

/**
 * Publishes View SQL's charts and the chart open in the editor into the header bridge while mounted.
 * Pass a memoised `sqlCharts` so an unchanged list does not republish.
 */
export function useRegisterStorySqlCharts(
  sqlCharts: StoryHeaderBridge["sqlCharts"],
  openEditorItemId: StoryHeaderBridge["openEditorItemId"],
  sqlTextItems?: StoryHeaderBridge["sqlTextItems"],
): void {
  const setSqlSlot = useContext(StorySqlContext);
  // The canvas is inside the page's StoryPathProvider; the header is not, so the path travels with the charts.
  const sqlIsNewPath = useStoryPath() === "new";
  useEffect(() => {
    if (!setSqlSlot) return;
    setSqlSlot({ sqlCharts, openEditorItemId, sqlTextItems, sqlIsNewPath });
  }, [sqlCharts, openEditorItemId, sqlTextItems, sqlIsNewPath, setSqlSlot]);
  useEffect(() => {
    if (!setSqlSlot) return;
    return () => setSqlSlot(null);
  }, [setSqlSlot]);
}

/** Publishes the story-level summary text into the header bridge while mounted (own slot, like View SQL). */
export function useRegisterStorySummary(summary: string | null): void {
  const setSlot = useContext(StorySummaryContext);
  useEffect(() => {
    if (!setSlot) return;
    setSlot(summary);
  }, [setSlot, summary]);
  useEffect(() => {
    if (!setSlot) return;
    return () => setSlot(null);
  }, [setSlot]);
}

/** Publishes the handler that opens the real export into the header bridge while mounted (own slot). Pass a stable callback. */
export function useRegisterOpenExport(onOpenExport: () => void): void {
  const setSlot = useContext(StoryOpenExportContext);
  useEffect(() => {
    if (!setSlot) return;
    setSlot(onOpenExport);
    return () => setSlot(null);
  }, [setSlot, onOpenExport]);
}

/** Publishes the canvas undo/redo into the header bridge while mounted (own slot, like View SQL). */
export function useRegisterStoryHistory(history: StoryHistorySlot): void {
  const setSlot = useContext(StoryHistoryContext);
  const { undo, redo, canUndo, canRedo } = history;
  useEffect(() => {
    if (!setSlot) return;
    setSlot({ undo, redo, canUndo, canRedo });
  }, [setSlot, undo, redo, canUndo, canRedo]);
  useEffect(() => {
    if (!setSlot) return;
    return () => setSlot(null);
  }, [setSlot]);
}

/** Publishes story-detail header branding while the Story Detail page is mounted. */
export function useRegisterStoryHeader(bridge: StoryHeaderBridge): void {
  const setBridge = useContext(StoryHeaderSetContext);
  useEffect(() => {
    if (!setBridge) return;
    setBridge(bridge);
    return () => {
      setBridge(null);
    };
  }, [bridge, setBridge]);
}

/** The story's save state for the top bar; null while no page publishes one. */
export interface StoryHeaderSaveState {
  status: SaveStatusLabelStatus;
  onRetry?: () => void;
}

const SaveStateContext = createContext<{
  saveState: StoryHeaderSaveState | null;
  setSaveState: (state: StoryHeaderSaveState | null) => void;
} | null>(null);

export const StoryHeaderSaveStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [saveState, setSaveState] = useState<StoryHeaderSaveState | null>(null);
  const value = useMemo(() => ({ saveState, setSaveState }), [saveState]);
  return <SaveStateContext.Provider value={value}>{children}</SaveStateContext.Provider>;
};

export function useStoryHeaderSaveState(): StoryHeaderSaveState | null {
  return useContext(SaveStateContext)?.saveState ?? null;
}

/**
 * Publishes the save state into the header while the page is mounted. `onRetry` is read through a ref,
 * so a new function each render does not republish.
 */
export function useRegisterStorySaveState(status: SaveStatusLabelStatus, onRetry?: () => void): void {
  const setSaveState = useContext(SaveStateContext)?.setSaveState;
  const retryRef = useRef(onRetry);
  retryRef.current = onRetry;
  const hasRetry = Boolean(onRetry);
  useEffect(() => {
    if (!setSaveState) return;
    setSaveState({ status, onRetry: hasRetry ? () => retryRef.current?.() : undefined });
  }, [status, hasRetry, setSaveState]);
  useEffect(() => () => setSaveState?.(null), [setSaveState]);
}
